import React, { useEffect, useRef, useState } from 'react'
import { useT } from '../lib/i18n'
import { useApp } from '../lib/context'
import { useQuery, type Report } from '../lib/api'

export default function SearchModal() {
  const t = useT()
  const { searchOpen, closeSearch } = useApp()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(0)

  const { data, loading } = useQuery<{ reports: Report[]; nextCursor: string | null }>(
    searchOpen ? `/timeline?limit=200` : '/timeline?limit=1'
  )

  const results = (data?.reports ?? []).filter((r) => {
    if (!query.trim()) return false
    const q = query.toLowerCase()
    return (
      r.projectId.includes(q) ||
      r.summaryMd.toLowerCase().includes(q) ||
      r.tasks.some((t) => t.title.toLowerCase().includes(q)) ||
      r.issues.some((i) => i.message.toLowerCase().includes(q))
    )
  }).slice(0, 20)

  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => inputRef.current?.focus(), 50)
      setQuery('')
      setSelected(0)
    }
  }, [searchOpen])

  useEffect(() => {
    setSelected(0)
  }, [query])

  if (!searchOpen) return null

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { closeSearch(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected((s) => Math.min(s + 1, results.length - 1)) }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)) }
  }

  function highlight(text: string, q: string): React.ReactNode {
    if (!q) return text
    const idx = text.toLowerCase().indexOf(q.toLowerCase())
    if (idx < 0) return text
    return (
      <>
        {text.slice(0, idx)}
        <mark style={{ background: 'var(--accent-dim)', color: 'var(--accent)', borderRadius: '2px' }}>
          {text.slice(idx, idx + q.length)}
        </mark>
        {text.slice(idx + q.length)}
      </>
    )
  }

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) closeSearch() }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '80px',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: '560px', maxWidth: '90vw',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          overflow: 'hidden',
        }}
        onKeyDown={handleKey}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '16px' }}>⌕</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('search')}
            style={{
              flex: 1, border: 'none', outline: 'none',
              background: 'transparent',
              color: 'var(--text)',
              fontFamily: 'var(--font-sans)',
              fontSize: '14px',
            }}
          />
          {loading && <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{t('loading')}</span>}
          <kbd
            onClick={closeSearch}
            style={{ padding: '2px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px', cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            Esc
          </kbd>
        </div>

        <div style={{ maxHeight: '400px', overflowY: 'auto', minHeight: 0 }}>
          {!query.trim() ? (
            <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: 'var(--text-dim)' }}>
              Tapez pour rechercher dans les rapports, tâches et erreurs…
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>
              {t('no_data')}
            </div>
          ) : (
            results.map((r, i) => (
              <div
                key={r.id}
                style={{
                  padding: '10px 16px',
                  borderBottom: '1px solid var(--border)',
                  background: selected === i ? 'var(--accent-dim)' : 'transparent',
                  cursor: 'pointer',
                }}
                onMouseEnter={() => setSelected(i)}
              >
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '3px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                    {r.projectId}
                  </span>
                  <span style={{ fontSize: '10px', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                    {r.startedAt.slice(0, 10)}
                  </span>
                  <span style={{
                    marginLeft: 'auto', fontSize: '10px',
                    color: r.status === 'error' ? 'var(--health-crit)' : r.status === 'warning' ? 'var(--health-warn)' : 'var(--health-good)',
                    fontFamily: 'var(--font-mono)', fontWeight: 600,
                  }}>
                    {r.status.toUpperCase()}
                  </span>
                </div>
                {r.summaryMd && (
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                    {highlight(r.summaryMd.replace(/^#+\s*/m, '').slice(0, 100), query)}
                  </div>
                )}
                {r.issues.filter((iss) => iss.message.toLowerCase().includes(query.toLowerCase())).map((iss, j) => (
                  <div key={j} style={{ fontSize: '11px', color: 'var(--health-crit)', marginTop: '2px' }}>
                    {highlight(iss.message.slice(0, 80), query)}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        <div style={{ padding: '6px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: '12px', fontSize: '10px', color: 'var(--text-dim)' }}>
          <span>↑↓ naviguer</span>
          <span>Entrée sélectionner</span>
          <span>Esc fermer</span>
        </div>
      </div>
    </div>
  )
}
